import React from 'react';
import {Cluster, ImageWrapper, Status, Table, TdLink} from '@pluto-tv/assemble';
import {IVodCollection} from 'models/vodCollections';
import vodCollectionRoutes from 'routes/programming.routes';
import {getPrefixedUrl} from 'routes';

interface IVodCollectionTableProps {
  id: string;
  isLoading: boolean;
  isError: boolean;
  isLazyLoading: boolean;
  items: Partial<IVodCollection>[];
  lazyLoad: () => void;
  handleReorderOnDrop?: (_from: string, _to: string, fromIndex: number[], toIndex: number) => void;
  selectedRows?: Partial<IVodCollection>[];
  setSelectedRows?: (rows: Partial<IVodCollection>[]) => void;
  draggable?: boolean;
  dragKey?: string;
  dropKeys?: string[];
  selectable?: string;
}

const VodCollectionTable = React.memo(
  ({
    id,
    items,
    handleReorderOnDrop,
    isError,
    isLazyLoading,
    isLoading,
    lazyLoad,
    draggable,
    dragKey,
    dropKeys,
    selectable,
    selectedRows,
    setSelectedRows,
  }: IVodCollectionTableProps) => {
    const tableProps = React.useMemo(
      () =>
        ({
          rows: items,
          loading: isLoading,
          lazyLoading: isLazyLoading,
          onLazyLoad: lazyLoad,
          lazyLoadingMsg: 'Loading additional Collections.',
          lazyLoadScrollOffset: 5,
          fixedHeader: true,
          wrapContent: true,
          draggable: draggable,
          dragKey: dragKey,
          dropKeys: dropKeys,
          onDrop: handleReorderOnDrop,
          selectable: selectable,
          onSelect: row => setSelectedRows?.(row),
          selected: selectedRows,
          id: id,
          emptyMsg: isError
            ? 'There was an error retrieving Collections associated. Please try again later.'
            : !items?.length && !isLoading
            ? 'No collections associated.'
            : undefined,
          cols: [
            {
              label: 'Collection Name',
              sortable: false,
              colMinWidth: '20rem',
              transform: row => (
                <Cluster wrap={false} space='small' align='center'>
                  <ImageWrapper width='3rem' height='3rem' src={row.imageFeatured?.path} alt={row.name} />
                  <TdLink
                    target='_blank'
                    row={row}
                    title={row.name || ''}
                    url={getPrefixedUrl(
                      vodCollectionRoutes.paths.vodCollectionEditDetailsPage.replace(':id', row?.id || ''),
                    )}
                  />
                </Cluster>
              ),
            },
            {
              label: 'Display Name',
              transform: row => row?.displayName,
              sortable: false,
              colMinWidth: '10rem',
            },
            handleReorderOnDrop
              ? {}
              : {
                  label: 'Published',
                  colMinWidth: '9.5rem',
                  transform: row => (
                    <Status
                      label={row.enabled ? 'Published' : 'Unpublished'}
                      state={row.enabled ? 'success' : 'neutral'}
                    />
                  ),
                },
            {
              label: 'Kids',
              sortable: false,
              colMinWidth: '6rem',
              transform: row => (
                <Status label={row.kidsMode ? 'Yes' : 'No'} state={row.kidsMode ? 'success' : 'neutral'} />
              ),
            },
            {
              label: 'Office Only',
              sortable: false,
              colMinWidth: '8rem',
              transform: row => (
                <Status
                  label={row.plutoOfficeOnly ? 'Yes' : 'No'}
                  state={row.plutoOfficeOnly ? 'success' : 'neutral'}
                />
              ),
            },
          ],
        } as any),
      [
        handleReorderOnDrop,
        id,
        isError,
        isLazyLoading,
        isLoading,
        items,
        lazyLoad,
        draggable,
        dragKey,
        dropKeys,
        selectable,
        selectedRows,
        setSelectedRows,
      ],
    );

    return <Table<Partial<IVodCollection>> {...tableProps} />;
  },
);

export default VodCollectionTable;
